import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-white text-black hover:bg-white/80",
        destructive: "bg-white text-red hover:bg-white/80",
        primary: "bg-white text-black hover:bg-white/80",
        secondary: "bg-bright-gray text-white hover:bg-bright-gray/80",
        ghost: "bg-transparent text-white font-normal",
        outline: "bg-transparent text-white border border-white",
        remote:
          "overflow-hidden bg-light-gray text-white uppercase hover:bg-light-gray/80 focus:bg-light-gray/80 disabled:opacity-60 active:bg-light-gray/80 rounded-full size-18 [&_svg]:size-8",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-7 rounded-md px-2",
        lg: "h-11 rounded-md px-8",
        icon: "size-10",
        remote: "size-18",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
